import { Box, Tabs, Text } from '@streamelements/frontend-ui';
import { useRouter, useRouterState } from '@tanstack/react-router';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { CompositionsService } from '../../services/CompositionsService';
import { useMemo } from 'react';

export const Route = createFileRoute('/_authenticated/settings/_layout')({
  component: SettingsLayout,
});

function SettingsLayout() {
  const routerState = useRouterState();
  const router = useRouter();
  const { matches } = routerState;
  const { pathname, search } = matches[matches.length - 1];

  const { data: videoCompositions } = useQuery({
    queryKey: ['video-compositions'],
    queryFn: async () => await CompositionsService.getActiveCompositions(),
  });

  const targetCanvasId = (() => {
    const { default: defaultComposition, ...others } = videoCompositions || ({} as never);
    void defaultComposition;

    return Object.keys(others)[0] ?? null;
  })();

  const hasCustomCanvases = !!targetCanvasId;

  const locations = useMemo(() => {
    return {
      outputsSettings: router.buildLocation({
        to: '/settings/outputs-settings',
      }),
      createFirstCanvas: router.buildLocation({
        to: '/settings/create-first-canvas',
      }),
      canvasSettings: router.buildLocation({
        to: '/settings/$canvasId',
        params: {
          canvasId: targetCanvasId,
        },
      }),
      broadcastSettings: router.buildLocation({
        to: '/settings/broadcast-settings',
        search: search as never,
      }),
    } as const;
  }, [search, targetCanvasId]);

  return (
    <>
      <Box css={{ pt: '1.5rem', pb: '0.5rem', mb: '1rem', px: '1.5rem' }}>
        <Text.Heading css={{ m: 0 }} weight="bold" level={5}>
          Settings
        </Text.Heading>
        <Text.Body css={{ color: '$textPrimary50' }}>Configure your Multi-streaming settings</Text.Body>
      </Box>

      <Box
        as={Tabs.Root}
        value={pathname.endsWith('/') ? pathname.slice(0, -1) : pathname}
        onValueChange={async (value) => {
          const { default: defaultComposition, ...others } = videoCompositions || ({} as never);
          void defaultComposition;

          const obj = {
            to: value,
            params: {
              canvasId: Object.keys(others)[0],
            },
            search,
          };

          router.navigate(obj);
        }}
        css={{ mb: '0.75rem' }}
      >
        <Tabs.List css={{ gap: '2rem', px: '1.5rem' }}>
          <Tabs.Trigger css={{ px: '0' }} value={locations.outputsSettings.pathname}>
            Outputs
          </Tabs.Trigger>
          <Tabs.Trigger
            css={{ px: '0' }}
            value={hasCustomCanvases ? locations.canvasSettings.pathname : locations.createFirstCanvas.pathname}
          >
            Canvas settings
          </Tabs.Trigger>
          <Tabs.Trigger css={{ px: '0' }} value={locations.broadcastSettings.pathname}>
            Broadcast settings
          </Tabs.Trigger>
        </Tabs.List>
      </Box>

      <Box css={{ flex: 1, overflow: 'hidden', pt: '0.125rem' }}>
        <Outlet />
      </Box>
    </>
  );
}
